import { useMutation } from '@tanstack/react-query';
import { type ReactElement, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';

import { Button } from '@autop/ui/src/button';

import { useSession } from '@/entities/session';
import { useSessionRefresh, useTRPC } from '@/shared/api';
import {
  clearSocialHandoff,
  PROVIDER_LABELS,
  type SocialProvider,
} from '@/shared/lib/social-handoff';
import { startSocialLogin } from '@/shared/lib/start-social-login';

type SessionCheck = 'checking' | 'signedIn' | 'signedOut';

interface SocialLinkConfirmProps {
  /** 어느 제공사로 왔는지. 기억하지 못했으면 null 이고, 그때는 제공사를 말하지 않는다. */
  provider: SocialProvider | null;
  /** 연결을 마치거나 미룬 뒤 돌아갈 사이트 안의 경로. */
  next: string;
}

/**
 * 소셜 계정 연결 확인 — 같은 이메일의 회원이 이미 있을 때 나오는 화면.
 *
 * **자동으로 잇지 않는 것이 설계 결정이다** (ADR-013). 제공자가 알려준 이메일이 같다는
 * 사실만으로 기존 계정을 넘겨주면, 그 이메일을 쥔 쪽이 곧 계정의 주인이 된다.
 * 그래서 서버는 **로그인한 본인의 요청만** 받는다.
 *
 * **비밀번호는 묻지 않는다** (오너 결정, 2026-09-10). 한때 물었으나 화면이 회원의 가입
 * 수단을 알 수 없어(`auth.me` 가 알려주지 않는다) "비밀번호로 가입하셨다면 입력하세요"
 * 라고 되물어야 했고, 소셜로만 가입한 사람에게는 아무 일도 하지 않는 빈칸이 계속 보였다.
 * 탈퇴(`WithdrawSection`)와 같은 판단이다.
 *
 * **잃은 것**: 로그인한 브라우저를 잠깐 만진 사람이 자기 소셜을 붙여 두면 그 뒤로 계속
 * 그 계정에 들어올 수 있다. 남은 방어는 로그인 상태 하나다.
 */
export function SocialLinkConfirm({ provider, next }: SocialLinkConfirmProps): ReactElement {
  const trpc = useTRPC();
  const providerLabel = provider === null ? null : PROVIDER_LABELS[provider];
  const navigate = useNavigate();
  const refresh = useSessionRefresh();
  const signIn = useSession((state) => state.signIn);
  const member = useSession((state) => state.member);

  const [check, setCheck] = useState<SessionCheck>('checking');

  // 이 화면은 로그인한 사람만 쓸 수 있으므로 세션부터 확인한다. 재발급기를 쓰는 이유는
  //   콜백 화면과 같다 — 부팅 복구와 겹쳐도 재발급이 한 번으로 합쳐진다.
  useEffect(() => {
    let isCancelled = false;

    void refresh().then(
      (signedInMember) => {
        if (isCancelled) return;
        signIn(signedInMember);
        setCheck('signedIn');
      },
      () => {
        if (!isCancelled) setCheck('signedOut');
      },
    );

    return () => {
      isCancelled = true;
    };
  }, [refresh, signIn]);

  // 연결했든 미뤘든 소셜 흐름은 여기서 끝난다 — 쪽지를 지우고 원래 가려던 곳으로 보낸다.
  const leave = () => {
    clearSocialHandoff();
    void navigate(next, { replace: true });
  };

  const link = useMutation(trpc.auth.social.link.mutationOptions({ onSuccess: leave }));

  if (check === 'checking') {
    return (
      <div className="u-container flex justify-center py-24">
        <p role="status" className="text-[14px] text-muted-foreground">
          확인하고 있습니다…
        </p>
      </div>
    );
  }

  if (check === 'signedOut') {
    return (
      <div className="u-container flex max-w-[30rem] flex-col items-center gap-5 py-24 text-center">
        <h1 className="text-[1.375rem] font-bold tracking-[-0.04em]">먼저 로그인해 주세요</h1>
        <p className="text-[14px] leading-[1.8] text-muted-foreground">
          이미 가입된 이메일입니다. 기존 방법으로 로그인하신 뒤 다시 시도하시면
          {providerLabel === null ? ' 이 소셜 계정을' : ` ${providerLabel} 계정을`} 연결할 수
          있습니다.
        </p>
        <Link
          to="/login"
          className="inline-flex h-11 items-center rounded-md bg-primary px-5 text-sm font-semibold text-primary-foreground hover:bg-primary/85"
        >
          로그인 화면으로
        </Link>
      </div>
    );
  }

  return (
    <div className="u-container flex justify-center py-16">
      <div className="flex w-full max-w-[26rem] flex-col gap-6">
        <div className="flex flex-col gap-2 text-center">
          <h1 className="text-[1.625rem] font-bold tracking-[-0.04em]">계정을 연결할까요?</h1>
          <p className="text-[14px] leading-[1.8] text-muted-foreground">
            지금 로그인한 계정 <b className="font-semibold text-foreground">{member?.email}</b> 에
            {providerLabel === null ? ' 이 소셜 계정을' : ` ${providerLabel} 계정을`} 연결합니다.
            다음부터는 두 방법 모두로 로그인할 수 있습니다.
          </p>
        </div>

        {link.error === null ? (
          <form
            className="flex flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              link.mutate({});
            }}
          >
            <Button type="submit" size="lg" className="w-full" disabled={link.isPending}>
              연결하기
            </Button>
            <Button type="button" variant="secondary" size="lg" className="w-full" onClick={leave}>
              나중에 하기
            </Button>
          </form>
        ) : (
          /**
           * 실패한 뒤에는 「연결하기」를 지운다.
           *
           * **티켓은 1회용이라 첫 시도에서 이미 소진된다** — 성공했든 거절당했든
           * 마찬가지다. 버튼을 남겨 두면 두 번째 클릭이 「가입 시간이 만료되었습니다」를
           * 불러 원래 사유를 덮어쓰고, 사용자는 무엇이 잘못됐는지 알 수 없게 된다.
           * 여기서 할 수 있는 일은 제공자부터 다시 거치는 것뿐이다.
           */
          <div className="flex flex-col gap-4">
            <p role="alert" className="text-[13px] text-destructive">
              {link.error.message}
            </p>
            <Button
              type="button"
              size="lg"
              className="w-full"
              onClick={() => startSocialLogin(provider ?? 'kakao', next, 'link')}
              disabled={provider === null}
            >
              다시 시도
            </Button>
            <Button type="button" variant="secondary" size="lg" className="w-full" onClick={leave}>
              나중에 하기
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
